'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useState, useCallback, useEffect } from 'react'
import { ListBulletIcon, HandThumbUpIcon as HandThumbUpIconSolid, XMarkIcon } from '@heroicons/react/16/solid'
import { HandThumbUpIcon } from '@heroicons/react/24/outline'

import { useGlobalStore } from '@/store/provider'
import { Common, Cruz } from '@cruz-com/lookup'
import { Button, Modal, Avatar, Divider } from '@/components'
import { myCruzApi } from '@/api'

type Props = {
  name: string
  id: string
  vote: Common.Vote | undefined
  user_id?: string
  my_user_id?: string
  onVoteChanged: () => void
} & ((React.ComponentPropsWithoutRef<'div'>))

// this is "like" button
export function Like({ name, id, vote, user_id, my_user_id, className, onVoteChanged }: Props) {
  const { user, modalSignIn, setModalSignIn } = useGlobalStore((state) => state)
  const [openModal, setOpenModal] = useState(false)
  const [shouldRefresh, setShouldRefresh] = useState(false)
  const [voteUp, setVoteUp] = useState(vote?.up)
  useEffect(() => {
    setVoteUp(vote?.up)
  }, [vote?.up])

  if (!vote) {
    return <></>
  }
  className = twMerge(
    'border border-zinc-300 dark:border-zinc-700 rounded h-10',
    'flex items-center divide-x dark:divide-zinc-700',
    className,
  )
  const btnStyle = twMerge(
    'px-3 h-full flex items-center disabled:cursor-not-allowed!',
    'hover:bg-zinc-100 dark:hover:bg-zinc-700 disabled:bg-transparent'
  )

  const c = useCallback(() => {
    return voteUp?.count || 0
  }, [voteUp])

  const isLiked = useCallback(() => {
    if (!my_user_id) {
      return false
    }
    return !_.isEmpty(_.find(voteUp?.user_list as Cruz.UserDisplay[], x => { return x?._id === my_user_id }))
  }, [voteUp])

  const isMine = useCallback(() => {
    return !_.isEmpty(my_user_id) && user_id === my_user_id
  }, [my_user_id, user_id])

  // toggle like
  async function toggleLike() {
    if (_.isEmpty(user)) {
      setShouldRefresh(true)
      setModalSignIn({ show: true, like: { name, id } })
      return
    }
    try {
      const { data } = await myCruzApi.toggleLike({ name, id })
      setVoteUp(data?.up)
      onVoteChanged()
    } catch {
    }
  }

  useEffect(() => {
    if (shouldRefresh && !modalSignIn?.show) {
      onVoteChanged()
    }
  }, [shouldRefresh, modalSignIn])

  return (
    <>
      <Modal open={openModal} setOpen={setOpenModal} className='min-w-[400px] min-h-24 p-0'>
        <div className='flex justify-between items-center bg-zinc-200 dark:bg-zinc-900 px-4 py-2 rounded-t-lg'>
          <div className='font-light'>
            <span className='font-semibold text-lg mr-1.5'>{c()}</span>
            {c() > 1 ? `people liked this` : ' person liked this'}
          </div>
          <Button type='button' variant='transparent' onClick={() => setOpenModal(false)} className='rounded-full -mr-2'>
            <XMarkIcon className='size-5' />
          </Button>
        </div>
        <Divider />
        <ul role='list' className='flex flex-col justify-center divide-y dark:divide-zinc-700 pb-2'>
          {
            _.map(voteUp?.user_list as Cruz.UserDisplay[], (d, i) => {
              const name = `${d.first_name} ${d.last_name}`
              return (
                <li key={i} className='py-4'>
                  <div className='flex items-center gap-x-4 px-4'>
                    <Avatar photo_url={d?.photo_url || ''} className='size-10' name={name} />
                    <div className='flex-none text-left'>
                      <div className='text-base font-medium'>{name}</div>
                      {
                        !_.isEmpty(d?.location) &&
                        <div className='text-sm font-light'>{d?.location?.city}, {d?.location?.state_abbr}</div>
                      }
                    </div>
                  </div>
                </li>
              )
            })
          }
        </ul>
      </Modal>
      <div className={className}>
        <button type='button'
          className={twMerge(btnStyle, 'rounded-l')}
          disabled={isMine()}
          title={isLiked() ? 'You liked this, click to Unlike' : 'Like'}
          onClick={() => toggleLike()}
        >
          {
            isLiked() ? <HandThumbUpIconSolid className='size-5 mr-2 text-red-800' /> : <HandThumbUpIcon className='size-5 mr-2' />
          }
          <span className='font-normal mr-1'>{c().toLocaleString()}</span>
          <span className='text-sm font-light'>Like{c() > 1 && 's'}</span>
        </button>
        {
          c() > 0 &&
          <button type='button' className={btnStyle} onClick={() => setOpenModal(true)} title='View who liked this'>
            <ListBulletIcon className='size-5' />
          </button>
        }
      </div>
    </>
  )
}


